import React from 'react'
import PropTypes from 'prop-types'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import Typography from '../common/Typography'


const CommissionReturnsChart = ({
  data = {},
  title = "Commission Returns",
  color = "#6c3260",
  groupBy = "agent",
  height = 400,
  showGrid = true,
  barSize = 32,
  yAxisFormat = "currency",
  viewType = "default",
  showTitle = true,
  showTotal = true,
  showPercentages = true,
  maxBars = null, // Allow all bars by default
  currency = "$",
  emptyStateMessage = "No commission data available"
}) => {

  const extractData = (responseData) => {
    if (!responseData) return []

    // Handle the specific structure from your API endpoint
    if (responseData.data?.byAgent && Array.isArray(responseData.data.byAgent)) {
      return responseData.data.byAgent
    }

    if (responseData.byAgent && Array.isArray(responseData.byAgent)) {
      return responseData.byAgent
    }

    if (Array.isArray(responseData)) return responseData

    if (responseData.data && Array.isArray(responseData.data)) return responseData.data

    return []
  }

  const processViewTypeData = (rawData, type) => {
    const extractedData = extractData(rawData)

    if (extractedData.length === 0) return []

    switch (type) {
      case "topPerformers":
        return processTopPerformersData(extractedData)
      case "byPercentage":
        return processByPercentageData(extractedData)
      case "monthly":
        return processMonthlyCommissionData(extractedData)
      default:
        return processCommissionData(extractedData)
    }
  }

  const processTopPerformersData = (rawData) => {
    const extractedData = extractData(rawData)

    if (extractedData.length === 0) return []

    // Sort by total commission (highest first) and take all or limited based on maxBars
    const sortedData = [...extractedData]
      .sort((a, b) => (b.total || 0) - (a.total || 0))

    const limitedData = maxBars ? sortedData.slice(0, maxBars) : sortedData

    return limitedData.map(agent => ({
      agentName: agent.agentName || 'Unknown Agent',
      total: agent.total || 0,
      percentage: parseFloat(agent.percentage || 0),
      displayName: (agent.agentName || 'UA').split(' ').map(name => name.charAt(0)).join('').toUpperCase()
    }))
  }

  const processByPercentageData = (rawData) => {
    const extractedData = extractData(rawData)

    if (extractedData.length === 0) return []

    // Sort by percentage (highest first)
    const sortedData = [...extractedData]
      .sort((a, b) => parseFloat(b.percentage || 0) - parseFloat(a.percentage || 0))

    const limitedData = maxBars ? sortedData.slice(0, maxBars) : sortedData

    return limitedData.map(agent => ({
      agentName: agent.agentName || 'Unknown Agent',
      total: agent.total || 0,
      percentage: parseFloat(agent.percentage || 0),
      displayName: (agent.agentName || 'UA').split(' ').map(name => name.charAt(0)).join('').toUpperCase()
    }))
  }

  const processMonthlyCommissionData = (rawData) => {
    const extractedData = extractData(rawData)

    if (extractedData.length === 0) return []

    // Handle monthly commission data structure
    if (extractedData[0] && extractedData[0].month && extractedData[0].commission !== undefined) {
      return extractedData
        .map(item => ({
          agentName: item.month,
          total: item.commission,
          percentage: item.percentage || 0,
          displayName: item.month?.substring(0, 3) || 'N/A'
        }))
        .slice(0, maxBars)
    }

    // Group by month if we have date information
    const groupedData = {}

    extractedData.forEach(item => {
      let key
      const date = new Date(item.date || item.createdAt || item.timestamp)

      switch (groupBy) {
        case "month":
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          key = monthNames[date.getMonth()]
          break
        case "quarter":
          const quarter = Math.floor(date.getMonth() / 3) + 1
          key = `Q${quarter}`
          break
        case "week":
          key = `Week ${Math.ceil(date.getDate() / 7)}`
          break
        default:
          key = item.agentName || 'Unknown Agent'
      }

      if (!groupedData[key]) {
        groupedData[key] = {
          total: 0,
          count: 0,
          percentages: []
        }
      }
      groupedData[key].total += item.total || item.amount || 0
      groupedData[key].count += 1
      groupedData[key].percentages.push(parseFloat(item.percentage || 0))
    })

    let result = Object.entries(groupedData).map(([agentName, data]) => ({
      agentName,
      total: data.total,
      percentage: data.percentages.length > 0 ? 
        data.percentages.reduce((a, b) => a + b, 0) / data.percentages.length : 0,
      displayName: agentName.substring(0, 3)
    }))

    // Sort and limit
    if (groupBy === "month") {
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      result.sort((a, b) => monthOrder.indexOf(a.agentName) - monthOrder.indexOf(b.agentName))
    }

    return maxBars ? result.slice(0, maxBars) : result
  }

  const processCommissionData = (rawData) => {
    const extractedData = extractData(rawData)

    if (extractedData.length === 0) return []

    // Handle the standard commission returns structure
    if (extractedData[0] && extractedData[0].agentName !== undefined) {
      const limitedData = maxBars ? extractedData.slice(0, maxBars) : extractedData
      return limitedData.map(agent => ({
        agentName: agent.agentName || 'Unknown Agent',
        total: agent.total || agent.amount || 0,
        percentage: parseFloat(agent.percentage || 0),
        displayName: (agent.agentName || 'UA').split(' ').map(name => name.charAt(0)).join('').toUpperCase()
      }))
    }

    // Fallback: create display data from any array
    const limitedData = maxBars ? extractedData.slice(0, maxBars) : extractedData
    return limitedData.map((item, index) => ({
      agentName: item.name || item.agentName || `Agent ${index + 1}`,
      total: item.total || item.amount || item.value || 0,
      percentage: parseFloat(item.percentage || item.rate || 0),
      displayName: (item.name || item.agentName || `A${index + 1}`).split(' ').map(name => name.charAt(0)).join('').toUpperCase()
    }))
  }

  const processedData = processViewTypeData(data, viewType)

  // Use processed data or empty array if no data
  const chartData = processedData.length > 0 ? processedData : []

  // Calculate Y-axis domain with proper scaling
  const maxValue = chartData.length > 0 ? Math.max(...chartData.map(item => item.total)) : 100000
  const yAxisMax = Math.ceil(maxValue * 1.15) // Add 15% padding at the top

  const formatYAxisLabel = (value) => {
    switch (yAxisFormat) {
      case "currency":
        if (value >= 1000000) {
          return `${currency}${(value / 1000000).toFixed(1)}M`
        }
        if (value >= 1000) {
          return `${currency}${(value / 1000).toFixed(0)}K`
        }
        return `${currency}${value}`
      case "k":
        if (value >= 1000) {
          return `${(value / 1000).toFixed(0)}k`
        }
        return value.toString()
      case "percentage":
        return `${value}%`
      default:
        return `${currency}${value}`
    }
  }

  const getViewTypeColor = (type) => {
    const colors = {
      topPerformers: "#6c3260",
      byPercentage: "#8B5CF6",
      monthly: "#3B82F6",
      default: color
    }
    return colors[type] || colors.default
  }

  const chartColor = getViewTypeColor(viewType)
  const totalAmount = data?.data?.total || data?.total || 0
  const avgPercentage = data?.data?.avgPercentage || data?.avgPercentage || null

  // Dynamic bar sizing based on number of agents
  const dynamicBarSize = chartData.length > 0 ? Math.min(50, Math.max(20, 300 / chartData.length)) : barSize

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-gray-800 text-white px-3 py-2 rounded-lg shadow-lg">
          <p className="text-sm font-medium">{data.agentName}</p>
          <p className="text-lg font-bold">{currency}{data.total.toLocaleString()}</p>
          {showPercentages && (
            <p className="text-sm text-gray-300">{data.percentage}% commission</p>
          )}
        </div>
      )
    }
    return null
  }

  // Show "No data available" message if no data
  if (chartData.length === 0) {
    return (
      <div className="w-full bg-white rounded-lg p-4 flex items-center justify-center" style={{ height: `${height}px` }}>
        <div className="text-center">
          <p className="text-gray-500 text-sm">{emptyStateMessage}</p>
        </div>
      </div>
    )
  }

  return (
    // Outer container matching the ViewingsBar design's card style
    <div className="w-full bg-white rounded-lg p-4">

      {/* Main Title */}
      {showTitle && (
        <h3 className="text-sm font-medium text-gray-800 mb-1">{title}</h3>
      )}

      {/* Total Amount Display */}
      {showTotal && (
        <div className="flex items-center gap-2 mb-2">
          <p className="text-md font-bold text-gray-800">{currency}{totalAmount.toLocaleString()}</p>
          {avgPercentage && (
            <span className="text-green-500 text-xs font-medium flex items-center">
              {avgPercentage.toFixed(1)}% avg ↗
            </span>
          )}
        </div>
      )}

      {/* Chart Container - matching ViewingsBar structure */}
      <div className="w-full" style={{ height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            // Adjusted margins to match the ViewingsBar design's alignment
            margin={{ top: 10, right: 15, left: -5, bottom: 5 }}
          >
            {/* Simplified CartesianGrid matching ViewingsBar */}
            <XAxis
              dataKey="displayName"
              axisLine={{ stroke: '#000', strokeWidth: 1 }} // Solid black line for X-Axis
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              padding={{ left: 15, right: 15 }} // Adjusted padding to align bars
            />
            <YAxis
                domain={[0, yAxisMax]}
                tickFormatter={formatYAxisLabel}
                axisLine={true} // Enable Y-axis line
                tickLine={true}
                tick={{ fontSize: 11, fill: '#666' }}
                width={30}
            />
            <Tooltip
              cursor={{ fill: `rgba(108, 50, 96, 0.1)` }} // Light fill matching the bar color
              contentStyle={{
                borderRadius: '6px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                backgroundColor: 'white',
                fontSize: '12px'
              }}
              labelStyle={{ fontWeight: '500', color: '#333', fontSize: '12px' }}
              itemStyle={{ color: chartColor, fontSize: '12px' }}
              content={<CustomTooltip />}
            />
            <Bar
              dataKey="total"
              fill={chartColor}
              barSize={dynamicBarSize}
              // Removed radius for squared tops as seen in the ViewingsBar design
              radius={0}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

CommissionReturnsChart.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  title: PropTypes.string,
  color: PropTypes.string,
  groupBy: PropTypes.oneOf(['agent', 'month', 'quarter', 'week']),
  height: PropTypes.number,
  showGrid: PropTypes.bool,
  barSize: PropTypes.number,
  yAxisFormat: PropTypes.oneOf(['currency', 'k', 'percentage']),
  viewType: PropTypes.oneOf(['default', 'topPerformers', 'byPercentage', 'monthly']),
  showTitle: PropTypes.bool,
  showTotal: PropTypes.bool,
  showPercentages: PropTypes.bool,
  maxBars: PropTypes.number,
  currency: PropTypes.string,
  emptyStateMessage: PropTypes.string
}

export default CommissionReturnsChart